// Shared upload-sync logic for both the Stimulus sync controller and the
// service worker Background Sync handler.
//
// Extracted from field_sync_controller.js (W5) so the service worker can call
// the same replay and orphan-cleanup code without duplicating it.
//
// All fetch calls include credentials: "include" -- required when called from
// the service worker context; harmless when called from a page context.

import db from "./field_db"
import { syncImagesFor, purgeUploaded, isLost, safeSetStatus } from "./field_image_queue"
import { logEvent, logError } from "./field_logger"
import { classifySyncResponse, WRITE_STATUS_SKIPPED_STALE, setCachedResponseStatus, LOCAL_STATUS_COMPLETION_ATTENTION } from "./field_sync_shared"
import { observedFetch, currentLevel } from "./field_network_health"
import { timeoutFor } from "./field_timeout_policy"
import { refreshRetryMeta } from "./field_submission_meta"
import { recordLedgerEntry, ACTIONS } from "./field_response_ledger"

// Card #4688 -- this response's current local status overlay (cached_responses
// row), used as the ledger's localStatusBefore/After for the writes below. A
// plain fetch/PUT never changes this value itself (only setCachedResponseStatus
// calls, which happen at the specific points marked below, do).
async function _localStatus(responseId) {
  const row = await db.cached_responses.where("responseId").equals(Number(responseId)).first().catch(() => null)
  return row?.status ?? null
}

// A record that keeps 5xx-ing this many times in a row goes terminal "failed"
// instead of retrying forever (Card #2434). Without a cap, one persistently
// bad record combined with the old break-on-first-retry loop behaviour would
// block every OTHER queued response indefinitely. 5 matches the retry cap
// already used for images (MAX_IMAGE_RETRIES, field_image_queue.js).
const MAX_CONSECUTIVE_SERVER_FAILURES = 5

// Card #4594 -- a queued completion (record.completeUrl present) that just
// went terminally "failed" -- rejected outright, or past the retry cap --
// will not resolve itself. Flip the local overlay from "done, sending" to
// "needs attention" so the place/Today/mission-list pages (and the response
// card's own badge) stop implying it is quietly on its way. No-op for a
// plain save (no completeUrl) -- there's nothing completion-shaped to flag.
function markCompletionAttention(record) {
  if (!record.completeUrl) return Promise.resolve()
  return setCachedResponseStatus(db, record.responseId, LOCAL_STATUS_COMPLETION_ATTENTION).catch(() => {})
}

// Replay a single pending response to the server.
//
// Accepts csrfToken as a parameter instead of reading it from document so
// this function can be called from the service worker (no DOM access).
//
// Returns:
//   "success"         -- response accepted by server and removed from queue
//   "reauth-required"  -- session expired/rotated mid-sync (401, CSRF-shaped
//                         422, or a followed redirect to the sign-in page);
//                         record marked reauth-required and retried
//                         automatically on the next sync pass (Card #2417)
//   "failed"          -- genuine validation error (4xx), or a record that
//                        exceeded MAX_CONSECUTIVE_SERVER_FAILURES; marked
//                        failed, not retried automatically
//   "retry"           -- network error or 5xx; leave in queue, try again
//                        later (the caller no longer stops the whole cycle
//                        on this -- Card #2434)
//   "skipped-stale"   -- a genuine 200, but the body's write_status flag
//                        (card #3804) says the server judged this payload
//                        stale and skipped the actual write. Kept in the
//                        queue (not deleted) rather than treated as synced --
//                        this is the client's only copy of these answers.
export async function replayResponse(record, csrfToken) {
  const url = `/api/v1/surveys/results/results.json?surveyable_id=${record.responseId}&surveyable_type=${record.surveyableType || "Response"}`

  // retry_count (card #2831) counts retries of THIS save specifically --
  // record.consecutiveFailures already tracks how many times this exact
  // queued PUT has come back 5xx (capped below at MAX_CONSECUTIVE_SERVER_FAILURES).
  // Network-error retries don't increment consecutiveFailures (see the catch
  // below), so this undercounts pure network-timeout retries -- accepted, the
  // card scopes retry_count to the save mechanism's own tracked counter, not a
  // new one. No-op if the record predates this card or was never on the field
  // app (refreshRetryMeta leaves data without _submission_meta untouched).
  const surveyData = refreshRetryMeta(record.surveyData, record.consecutiveFailures || 0)

  // Card #4688 -- the ledger's localStatusBefore for this write. A plain PUT
  // never itself changes this overlay (only the completion branch below does).
  const localStatusBefore = await _localStatus(record.responseId)

  let response
  try {
    response = await observedFetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      credentials: "include",
      body: JSON.stringify(surveyData),
      signal: AbortSignal.timeout(timeoutFor("json_write", currentLevel()))
    }, { kind: "json_write", sizeClass: "small" })
  } catch {
    // Network failure -- leave in queue, try again next cycle
    logEvent("sync", "Sync paused — network unstable", { responseId: record.responseId }).catch(() => {})
    recordLedgerEntry(record.responseId, {
      action: ACTIONS.ANSWERS_SYNC, localStatusBefore, localStatusAfter: localStatusBefore
    }).catch(() => {})
    return "retry"
  }

  // Card #4688 -- one ledger entry for this write, whichever branch below
  // handles the outcome. The PUT alone never changes the local status overlay.
  recordLedgerEntry(record.responseId, {
    action:            ACTIONS.ANSWERS_SYNC,
    localStatusBefore,
    localStatusAfter:  localStatusBefore,
    httpStatus:        response.status,
    serverStatus:      response.headers.get("X-WIN-Response-Status"),
    requestId:         response.headers.get("X-Request-Id")
  }).catch(() => {})

  const outcome = await classifySyncResponse(response)

  if (outcome === "reauth-required") {
    await db.pending_responses.update(record.id, {
      status: "reauth-required",
      reauthAt: new Date().toISOString(),
      failureStatus: response.status
    })
    logEvent("sync", "Sync paused — sign in required", { responseId: record.responseId, status: response.status }).catch(() => {})
    return "reauth-required"
  }

  if (outcome === "success") {
    // Card #3804 -- a genuine 200 can still mean the write itself was
    // skipped as stale (skip_stale_result_write?, results_controller.rb).
    // Previously indistinguishable from a landed write, so this branch went
    // straight on to fire completeUrl (ending the response on OLD data) and
    // then deleted the queued record below, as if the tasker's answers had
    // been saved. Bail out here instead -- keep the record, don't complete.
    let body = null
    try { body = await response.clone().json() } catch {}

    if (body?.write_status === WRITE_STATUS_SKIPPED_STALE) {
      await db.pending_responses.update(record.id, {
        status: "skipped-stale",
        skippedAt: new Date().toISOString()
      })
      logEvent("sync", "Response write skipped as stale by server, keeping local copy", {
        responseId: record.responseId
      }).catch(() => {})
      return "skipped-stale"
    }

    // Card #4594 -- carried through to the final "Response submitted" log so
    // a genuine completion is distinguishable from a plain save that never
    // needed one (see that log below). null means "no completion step ran".
    let completionStatus = null

    // If there's a completeUrl, fire it now (response completion). Its normal
    // success response is an HTML redirect, not JSON (Field::ResponsesController
    // #complete), so classify with expectJson: false -- the reauth check here
    // relies on the redirected-to-sign-in-page detection, not body shape
    // (Card #2418: response.ok alone is not enough, a followed redirect to
    // the login page resolves as a "successful" 200).
    if (record.completeUrl) {
      let completeResponse
      try {
        completeResponse = await observedFetch(record.completeUrl, {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken },
          credentials: "include",
          signal: AbortSignal.timeout(timeoutFor("json_write_light", currentLevel()))
        }, { kind: "json_write", sizeClass: "small" })
      } catch {
        // Complete call failed -- data saved but not marked complete. Leave in queue.
        logEvent("sync", "Sync paused — network unstable (completion)", { responseId: record.responseId }).catch(() => {})
        recordLedgerEntry(record.responseId, {
          action: ACTIONS.COMPLETE, localStatusBefore, localStatusAfter: localStatusBefore
        }).catch(() => {})
        return "retry"
      }

      completionStatus = completeResponse.status
      const completeOutcome = await classifySyncResponse(completeResponse, { expectJson: false })

      // Card #4688 -- one ledger entry for this write. #complete always
      // redirects (Field::ResponsesController#complete), so fetch's default
      // redirect: "follow" means completeResponse is the LANDED page's
      // response, not this POST's own -- requestId is left null rather than
      // attribute the landed page's request id to this write (see
      // field_response_ledger.js's header comment). serverStatus is read
      // anyway: it is simply absent on the landed page, never wrong.
      recordLedgerEntry(record.responseId, {
        action:            ACTIONS.COMPLETE,
        localStatusBefore,
        localStatusAfter:  completeOutcome === "success" ? "ended" : localStatusBefore,
        httpStatus:        completeResponse.status,
        serverStatus:      completeResponse.headers.get("X-WIN-Response-Status"),
        requestId:         null
      }).catch(() => {})

      if (completeOutcome === "reauth-required") {
        await db.pending_responses.update(record.id, {
          status: "reauth-required",
          reauthAt: new Date().toISOString(),
          failureStatus: completeResponse.status
        })
        logEvent("sync", "Sync paused — sign in required (completion)", { responseId: record.responseId }).catch(() => {})
        return "reauth-required"
      }
      if (completeOutcome === "retry") {
        // Card #4594 -- this sub-call had NO cap of its own before: the PUT
        // above can succeed (resetting nothing here) while the complete POST
        // keeps 5xx-ing forever, leaving the record "retry" indefinitely --
        // one of the three causes this card's investigation notes named for
        // a completion that "never ends" (completeUrl missing, POST not
        // sent, or complete returning without ending). Reuses the SAME
        // consecutiveFailures field and cap as the PUT's own retry handling
        // below, so a response whose data saves fine but whose completion
        // never does still goes terminally "failed" instead of retrying
        // silently forever.
        const consecutiveFailures = (record.consecutiveFailures || 0) + 1
        if (consecutiveFailures >= MAX_CONSECUTIVE_SERVER_FAILURES) {
          await db.pending_responses.update(record.id, {
            status: "failed",
            failedAt: new Date().toISOString(),
            failureStatus: completeResponse.status,
            consecutiveFailures
          })
          await markCompletionAttention(record)
          logError("sync", "Response completion failed after repeated server errors", {
            responseId: record.responseId, status: completeResponse.status, consecutiveFailures
          }).catch(() => {})
          return "failed"
        }
        await db.pending_responses.update(record.id, { consecutiveFailures }).catch(() => {})
        return "retry"
      }
      if (completeOutcome === "failed") {
        // Completion rejected outright -- data is saved but not marked ended
        // server-side. Surface this rather than silently deleting the local
        // record as if it fully synced (Card #2418).
        await db.pending_responses.update(record.id, {
          status: "failed",
          failedAt: new Date().toISOString(),
          failureStatus: completeResponse.status
        })
        await markCompletionAttention(record)
        logError("sync", "Response completion rejected", { responseId: record.responseId, status: completeResponse.status }).catch(() => {})
        return "failed"
      }
      // completeOutcome === "success" -- fall through to delete below.
      // Card #3827 -- the offline shell and Today tab read status from
      // cached_responses; without this they kept showing it in progress.
      await setCachedResponseStatus(db, record.responseId, "ended").catch(() => {})
    }

    await db.pending_responses.delete(record.id)
    // Card #4594 -- this log previously read identically whether or not a
    // completion step ran: a plain save and a genuine completion (even one
    // whose completeUrl went missing some other way) both logged bare
    // "Response submitted". That is the exact shape of the "good and bad
    // look the same" gap this card's investigation notes described -- two
    // responses synced every answer and photo, logged this same line, and
    // still sat "scheduled" server-side. completion/completionStatus make a
    // future instance of that pattern show up here instead of hiding behind
    // an identical success log.
    logEvent("sync", "Response submitted", {
      responseId:       record.responseId,
      missionName:      record.missionName || null,
      completion:       record.completeUrl ? "applied" : "none",
      completionStatus: completionStatus
    }).catch(() => {})
    return "success"
  }

  if (outcome === "failed") {
    // Genuine validation error -- mark as failed so user can see it, don't retry automatically
    await db.pending_responses.update(record.id, {
      status: "failed",
      failedAt: new Date().toISOString(),
      failureStatus: response.status
    })
    await markCompletionAttention(record)
    logError("sync", "Response submission failed", { responseId: record.responseId, status: response.status }).catch(() => {})
    return "failed"
  }

  // outcome === "retry" (5xx). Cap consecutive failures (Card #2434) -- past
  // the cap this becomes a visible "failed" record using the SAME manual
  // review affordance as a real validation failure (field_offline_list_controller.js
  // already renders "failed" pending_responses with a "will retry when
  // connected" prompt in the offline task list).
  const consecutiveFailures = (record.consecutiveFailures || 0) + 1
  if (consecutiveFailures >= MAX_CONSECUTIVE_SERVER_FAILURES) {
    await db.pending_responses.update(record.id, {
      status: "failed",
      failedAt: new Date().toISOString(),
      failureStatus: response.status,
      consecutiveFailures
    })
    await markCompletionAttention(record)
    logError("sync", "Response submission failed after repeated server errors", {
      responseId: record.responseId, status: response.status, consecutiveFailures
    }).catch(() => {})
    return "failed"
  }

  await db.pending_responses.update(record.id, { consecutiveFailures }).catch(() => {})
  return "retry"
}

// Full-sync path for one pending response: upload any unresolved images,
// substitute blob: URLs in the survey data, persist the substituted data,
// purge uploaded blobs, then replay the response.
//
// This is the canonical path for background-sync and any other caller that
// needs to replay a single response end-to-end (Bug fix: SW was calling
// replayResponse directly, bypassing URL substitution).
export async function syncImagesAndReplay(record, csrfToken) {
  // Card #3798 -- this used to swallow any throw from syncImagesFor with zero
  // trace, one of the confirmed-real silent paths behind the never-draining
  // iOS queue. Still falls back to an empty map (a response must be able to
  // replay even if its images never resolved), but now logs first so the
  // failure is visible in app_logs rather than invisible.
  const { urlMap, uploadedIds, attachedIds } = await syncImagesFor(record.responseId, csrfToken)
    .catch((error) => {
      logError("sync", "Image sync failed before response replay", {
        responseId: record.responseId,
        name:       error?.name,
        message:    error?.message
      }).catch(() => {})
      return { urlMap: {}, uploadedIds: [], attachedIds: [] }
    })

  let surveyData = record.surveyData
  if (Object.keys(urlMap).length > 0) {
    surveyData = substituteUrls(surveyData, urlMap)
    // Persist substituted data so a crash-and-retry sends the clean version
    try {
      await db.pending_responses.update(record.id, { surveyData })
    } catch { /* best effort */ }
  }

  // Purge is gated on the RESPONSE being durably on the server (W2, ADR-0048 §3).
  // We replay FIRST and only purge the banked blobs after a confirmed "success".
  // On "retry" / "failed" the banked copies stay as the safety net -- they are
  // the only recovery path if the response never lands.
  const outcome = await replayResponse({ ...record, surveyData }, csrfToken)

  // Card #3827 -- a "success" replay replaced result_json wholesale with the
  // answers it CARRIED. Purge a photo only when those answers hold its server
  // URL. "Attached at upload" alone is not proof any more: this replay may
  // just have overwritten that attach (B1). A photo the replayed answers
  // don't hold keeps its local copy.
  if (outcome === "success" && uploadedIds.length > 0) {
    const replayed = JSON.stringify(surveyData || {})
    const images   = (await db.pending_images.bulkGet(uploadedIds).catch(() => [])).filter(Boolean)
    const confirmed = []
    for (const image of images) {
      if (image.serverUrl && replayed.includes(image.serverUrl)) {
        confirmed.push(image.id)
      } else if (attachedIds.includes(image.id)) {
        logEvent("sync", "Replayed answers do not hold an attached photo, keeping local copy", {
          responseId:   record.responseId,
          questionName: image.questionName || null
        }).catch(() => {})
      }
    }
    if (confirmed.length > 0) await purgeUploaded(confirmed).catch(() => {})
  }

  return outcome
}

// Syncs pending images that have no matching pending_response.
// These are "orphaned" -- their parent response was synced on a prior pass
// but the image upload failed (network error, timeout). We re-attempt here
// and patch the server result with any newly resolved URLs.
//
// Accepts csrfToken as a parameter (see replayResponse).
export async function syncOrphanedImages(csrfToken) {
  // Card #2864 -- "failed" is deliberately EXCLUDED from this pickup query.
  // It used to be included, which retried an already-terminal failure (a
  // genuine 4xx, or MAX_IMAGE_RETRIES exhausted, or an unreadable blob that
  // will never become readable) forever -- the same dead 404 re-firing for
  // days instead of surfacing once. This mirrors the convention already used
  // for pending_responses (field_sync_controller.js's own pickup queries
  // never include "failed" either -- replayResponse's "failed" branch is
  // explicitly "don't retry automatically"). A tasker can still manually
  // retry a failed image via the My Photos backup affordance
  // (field_photos_controller.js#backup -> backupOrphan), which is unaffected.
  // Card #3827 -- a lost row (unreadable or dropped blob) never uploads, so
  // it never counts as an orphan to sync. Its status is "failed" and already
  // excluded above; the filter keeps that true if a lost row ever reads
  // otherwise.
  const waiting = await db.pending_images
    .where("status").anyOf(["pending", "reauth-required"])
    .filter(img => !isLost(img))
    .toArray()
  if (waiting.length === 0) return

  // Card #3827 (B1) -- a photo whose response still has a queued replay is
  // NOT an orphan: syncImagesAndReplay uploads it, substitutes its server URL
  // into the queued answers, replays, then purges. Uploading it here first
  // left those answers holding the blob: link, and the replay's wholesale PUT
  // overwrote the attach with it.
  const queuedResponseIds = await _queuedReplayResponseIds()
  const orphaned = waiting.filter(img => !queuedResponseIds.has(String(img.responseId ?? img.surveyableId)))
  const skippedResponses = new Set(
    waiting.filter(img => !orphaned.includes(img)).map(img => String(img.responseId ?? img.surveyableId))
  ).size

  // Card #3799 -- extend the payload beyond a bare count so this log itself
  // carries the same "what's waiting and how old" shape as emitQueueSnapshot,
  // scoped to just this pass's orphans rather than the whole queue.
  const byKind = { queued: 0, shadow: 0 }
  let reauth = 0
  let oldestQueuedAt = null
  for (const img of orphaned) {
    if (img.kind in byKind) byKind[img.kind]++
    if (img.status === "reauth-required") reauth++
    if (!oldestQueuedAt || img.queuedAt < oldestQueuedAt) oldestQueuedAt = img.queuedAt
  }
  const oldestAgeHours = oldestQueuedAt
    ? Math.round((Date.now() - new Date(oldestQueuedAt).getTime()) / (60 * 60 * 1000))
    : 0

  logEvent("sync", "Syncing orphaned images", {
    count: orphaned.length,
    queued: byKind.queued,
    shadow: byKind.shadow,
    reauth,
    oldestAgeHours,
    skippedResponses
  }).catch(() => {})
  if (orphaned.length === 0) return

  // Group by surveyableId so we can patch results in one pass per response
  const byId = {}
  for (const img of orphaned) {
    if (!byId[img.surveyableId]) byId[img.surveyableId] = []
    byId[img.surveyableId].push(img)
  }

  for (const surveyableId of Object.keys(byId)) {
    // Card #3798 -- same logged fallback as syncImagesAndReplay above. Left
    // silent, this was one record's failure repeatedly re-entering
    // "Syncing orphaned images" on every subsequent pass with no evidence of
    // WHY it never resolved -- exactly the pattern SIGNAL's evidence showed.
    const { uploadedIds, attachedIds } = await syncImagesFor(surveyableId, csrfToken)
      .catch((error) => {
        logError("sync", "Orphaned image sync failed", {
          surveyableId,
          name:    error?.name,
          message: error?.message
        }).catch(() => {})
        return { urlMap: {}, uploadedIds: [], attachedIds: [] }
      })
    // Direct library uploads (surveyableId "0") have no owning response and no
    // survey data to patch -- the confirmed 2xx from the backup endpoint IS the
    // durable server storage, so they purge immediately (ADR-0048 §3 satisfied).
    if (surveyableId === "0") {
      if (uploadedIds.length > 0) await purgeUploaded(uploadedIds).catch(() => {})
      continue
    }
    // Card #3827 -- the server already wrote these into the answer at upload
    // (blob: slot patched, or photo attached to its question), so no client
    // PUT-back: that GET/PUT round trip is what used to persist preview URLs.
    // They are NOT purged here (B1, ADR-0054's June amendment: purge waits
    // for the whole response). An open editor or offline shell can still hold
    // the blob: link, and its next full save must be able to swap in the
    // server URL (substituteUploadedUrls) rather than overwrite the attach.
    // The row stays "uploaded" + attachedAt until purgeForResponse (submit),
    // a replay that carries its URL, or purgeStale's 7-day sweep.
    const attached    = new Set(attachedIds)
    const unconfirmed = uploadedIds.filter(id => !attached.has(id))
    if (unconfirmed.length === 0) continue

    const images = (await db.pending_images.bulkGet(unconfirmed).catch(() => [])).filter(img => img?.serverUrl)
    if (images.length === 0) continue
    const pendingUrlMap = Object.fromEntries(images.map(img => [img.localUrl, img.serverUrl]))

    // Patch the server result to replace stale blob: URLs with real server URLs.
    // Only purge the local blobs once the server answer is confirmed to hold
    // them (ADR-0048 §3, ADR-0054). If it can't be confirmed, keep the blob.
    // Card #3827 (B1) -- confirmed the same as an attach, so kept the same
    // way (stamped attachedAt, not purged) for the same reason.
    const patched = await patchResultUrls(surveyableId, pendingUrlMap, csrfToken)
    if (patched) {
      const attachedAt = new Date().toISOString()
      for (const img of images) await safeSetStatus(img.id, { attachedAt })
    }
  }
}

// Response ids (as strings) with a queued replay still to run. pending_responses
// holds either the editor's string id or the offline shell's number.
async function _queuedReplayResponseIds() {
  const rows = await db.pending_responses
    .where("status").anyOf(["pending", "reauth-required"])
    .toArray()
    .catch(() => [])
  return new Set(rows.map(r => String(r.responseId)))
}

// Fetches the current result from the server, substitutes blob: URLs with
// server URLs, and PUTs the patched data back. Returns true when the server
// copy is known durable (patched OK, or its stored answer already holds every
// server URL), false when it could not be confirmed -- the caller must NOT
// purge in that case.
//
// Card #3827 -- reads raw=1 (result_json as stored). The default GET swaps
// in signed preview URLs and recovers blob: slots for display, so PUTting it
// back persisted preview URLs no Image matches, and an unpatched blob: slot
// looked "already clean". A skipped_stale PUT is also not confirmation.
//
// Accepts csrfToken as a parameter (see replayResponse).
export async function patchResultUrls(surveyableId, urlMap, csrfToken) {
  try {
    const getRes = await observedFetch(
      `/api/v1/surveys/results/results.json?surveyable_id=${surveyableId}&surveyable_type=Response&raw=1`,
      { headers: { Accept: "application/json" }, credentials: "include", signal: AbortSignal.timeout(timeoutFor("json_read", currentLevel())) },
      { kind: "json_read", sizeClass: "small" }
    )
    // A followed redirect to the sign-in page or a CSRF-shaped 422 (session
    // expired mid-patch) must NOT be treated as durable -- classify rather
    // than trust response.ok alone (Card #2417/#2418).
    if ((await classifySyncResponse(getRes)) !== "success") {
      logEvent("sync", "Patch result URLs skipped — could not confirm current result", {
        surveyableId
      }).catch(() => {})
      return false
    }
    const data = await getRes.json()
    const patched = substituteUrls(data.result, urlMap)
    if (JSON.stringify(patched) === JSON.stringify(data.result)) {
      // Nothing to substitute. Safe to purge only if the stored answer
      // actually holds every server URL -- "no blob: here" alone used to
      // purge photos whose answer never reached the server at all.
      if (resultReferencesAll(data.result, Object.values(urlMap))) return true
      logEvent("sync", "Patch result URLs skipped — answer does not reference photo, keeping local copy", {
        surveyableId, count: Object.keys(urlMap).length
      }).catch(() => {})
      return false
    }
    const putRes = await observedFetch(
      `/api/v1/surveys/results/results.json?surveyable_id=${surveyableId}&surveyable_type=Response`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "include",
        body: JSON.stringify(patched),
        signal: AbortSignal.timeout(timeoutFor("json_write_light", currentLevel()))
      },
      { kind: "json_write", sizeClass: "small" }
    )
    // Card #4688 -- the "photo link" write: patching a resolved server photo
    // URL back into the stored result. Never changes the response's own
    // lifecycle status, so localStatusBefore/After are the same value.
    const photoLinkLocalStatus = await _localStatus(surveyableId)
    recordLedgerEntry(surveyableId, {
      action:            ACTIONS.PHOTO_LINK,
      localStatusBefore: photoLinkLocalStatus,
      localStatusAfter:  photoLinkLocalStatus,
      httpStatus:        putRes.status,
      serverStatus:      putRes.headers.get("X-WIN-Response-Status"),
      requestId:         putRes.headers.get("X-Request-Id")
    }).catch(() => {})

    const patchOk = (await classifySyncResponse(putRes)) === "success"
    if (!patchOk) {
      logError("sync", "Patch result URLs failed — server rejected patch", {
        surveyableId, status: putRes.status
      }).catch(() => {})
      return false
    }
    let body = null
    try { body = await putRes.clone().json() } catch {}
    if (body?.write_status === WRITE_STATUS_SKIPPED_STALE) {
      logEvent("sync", "Patch result URLs skipped as stale by server, keeping local copy", {
        surveyableId
      }).catch(() => {})
      return false
    }
    return true
  } catch {
    // Card #3799 -- this catch previously returned false with zero trace;
    // the caller correctly retains the local blob (safety net intact), but
    // server-side this pass just vanished rather than showing up as an
    // attempted-and-failed patch.
    logError("sync", "Patch result URLs failed: network error", { surveyableId }).catch(() => {})
    return false // best effort -- could not confirm, do not purge
  }
}

// True when every url appears somewhere in the result (card #3827).
export function resultReferencesAll(result, urls) {
  if (!result || urls.length === 0) return false
  const json = JSON.stringify(result)
  return urls.every(url => typeof url === "string" && json.includes(url))
}

// Card #3827 (B1) -- { localUrl -> serverUrl } for this response's photos that
// are already on the server (status "uploaded"). The one place a full save's
// answers get their blob: links swapped for server URLs outside the replay:
// the online editor's save and queue paths (result_editor_controller.js).
export async function uploadedUrlMapFor(surveyableId) {
  const rows = await db.pending_images
    .where("surveyableId").equals(String(surveyableId))
    .filter(r => r.status === "uploaded" && !!r.serverUrl && !!r.localUrl)
    .toArray()
  return Object.fromEntries(rows.map(r => [r.localUrl, r.serverUrl]))
}

// A copy of `data` with every uploaded photo's blob: link replaced by its
// server URL, for a request body -- the caller's object is never changed.
export async function substituteUploadedUrls(surveyableId, data) {
  const urlMap = await uploadedUrlMapFor(surveyableId)
  if (!data || Object.keys(urlMap).length === 0) return data
  return substituteUrls(data, urlMap)
}

// Recursively replace blob: URLs in survey data with server URLs.
// Survey data is a plain object keyed by question name; values may be
// strings (URLs), arrays of strings, or nested objects.
export function substituteUrls(data, urlMap) {
  if (!data || !Object.keys(urlMap).length) return data
  const json = JSON.stringify(data)
  const substituted = Object.entries(urlMap).reduce(
    (str, [local, server]) => str.replaceAll(local, server),
    json
  )
  try {
    return JSON.parse(substituted)
  } catch {
    return data // parse failed, return original
  }
}
